import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { SapTableLinkedService } from 'app/model/linkedservice/sap-table.linkedservice';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { ValueAdapter } from '../value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SapTableLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                systemNumber: '*',
                messageServer: '*',
                messageServerService: '*',
                systemId: '*',
                logonGroup: '*',
                clientId: '*',
                language: '*',
                sncMode: '*',
                sncMyName: '*',
                sncPartnerName: '*',
                sncLibraryPath: '*',
                sncQop: '*',
                userName: '*',
                password: 'secret',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class SapTableLinkedServiceAdapter extends LinkedServiceAdapter<SapTableLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => SapTableLinkedService {
        return SapTableLinkedService;
    }

    protected model(): IModel {
        return SapTableLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SapTableLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SapTableLinkedService;

        properties.type = Metamodel.modelFor(SapTableLinkedService).name;

        properties.typeProperties = {
            clientId: this.valueToResource(linkedService, SapTableLinkedService.CLIENT_ID),
            language: this.valueToResource(linkedService, SapTableLinkedService.LANGUAGE) || undefined,
            sncMode: linkedService.sncMode,
            userName: this.valueToResource(linkedService, SapTableLinkedService.USER_NAME),
            password: parameterizablePasswordOrAKVtoSecretBase(linkedService, SapTableLinkedService.PASSWORD, this.context)
        };

        if (linkedService.logonType === 'applicationServer') {
            properties.typeProperties.server = this.valueToResource(linkedService, SapTableLinkedService.SERVER);
            properties.typeProperties.systemNumber = this.valueToResource(linkedService, SapTableLinkedService.SYSTEM_NUMBER);
        } else {
            properties.typeProperties.messageServer = this.valueToResource(linkedService, SapTableLinkedService.MESSAGE_SERVER);
            properties.typeProperties.messageServerService = this.valueToResource(linkedService, SapTableLinkedService.MESSAGE_SERVER_SERVICE);
            properties.typeProperties.systemId = this.valueToResource(linkedService, SapTableLinkedService.SYSTEM_ID);
            properties.typeProperties.logonGroup = this.valueToResource(linkedService, SapTableLinkedService.LOGON_GROUP);
        }

        if (linkedService.sncMode) {
            properties.typeProperties.sncMyName = this.valueToResource(linkedService, SapTableLinkedService.SNC_MY_NAME);
            properties.typeProperties.sncPartnerName = this.valueToResource(linkedService, SapTableLinkedService.SNC_PARTNER_NAME);
            properties.typeProperties.sncLibraryPath = this.valueToResource(linkedService, SapTableLinkedService.SNC_LIBRARY_PATH);
            properties.typeProperties.sncQop = linkedService.sncQop;
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SapTableLinkedService): Promise<SapTableLinkedService> {
        const prop = linkedServiceResource.properties as Model.SapTableLinkedService;
        if (prop.typeProperties != null) {
            linkedService.logonType = !stringIsNullOrEmpty(prop.typeProperties.messageServer) ? 'group' : 'applicationServer';
            if (linkedService.logonType === 'applicationServer') {
                ValueAdapter.toModel(linkedService, SapTableLinkedService.SERVER, prop.typeProperties.server);
                ValueAdapter.toModel(linkedService, SapTableLinkedService.SYSTEM_NUMBER, prop.typeProperties.systemNumber);
            } else {
                ValueAdapter.toModel(linkedService, SapTableLinkedService.MESSAGE_SERVER, prop.typeProperties.messageServer);
                ValueAdapter.toModel(linkedService, SapTableLinkedService.MESSAGE_SERVER_SERVICE, prop.typeProperties.messageServerService);
                ValueAdapter.toModel(linkedService, SapTableLinkedService.SYSTEM_ID, prop.typeProperties.systemId);
                ValueAdapter.toModel(linkedService, SapTableLinkedService.LOGON_GROUP, prop.typeProperties.logonGroup);
            }

            ValueAdapter.toModel(linkedService, SapTableLinkedService.CLIENT_ID, prop.typeProperties.clientId);
            ValueAdapter.toModel(linkedService, SapTableLinkedService.LANGUAGE, prop.typeProperties.language);

            linkedService.sncMode = prop.typeProperties.sncMode != null ? prop.typeProperties.sncMode : false;
            if (linkedService.sncMode) {
                ValueAdapter.toModel(linkedService, SapTableLinkedService.SNC_MY_NAME, prop.typeProperties.sncMyName);
                ValueAdapter.toModel(linkedService, SapTableLinkedService.SNC_PARTNER_NAME, prop.typeProperties.sncPartnerName);
                ValueAdapter.toModel(linkedService, SapTableLinkedService.SNC_LIBRARY_PATH, prop.typeProperties.sncLibraryPath);
                linkedService.sncQop = prop.typeProperties.sncQop;
            }

            ValueAdapter.toModel(linkedService, SapTableLinkedService.USER_NAME, prop.typeProperties.userName);
            parameterizableSecretBaseToModel(linkedService, SapTableLinkedService.PASSWORD, prop.typeProperties.password, this.context, true);

        }
        return linkedService;
    }
}
